<script module lang="ts">
	export enum InlineMessageType {
		INFO = 'info',
		WARNING = 'warning',
		ERROR = 'error',
		SUCCESS = 'success'
	}
</script>

<script lang="ts">
	import { Icon } from '@repo/commons/components'
	import type { Snippet } from 'svelte'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'

	interface Props {
		type?: InlineMessageType
		message?: string
		children?: Snippet
	}

	const ICONS: Record<InlineMessageType, IconName> = {
		[InlineMessageType.INFO]: 'info',
		[InlineMessageType.WARNING]: 'warning',
		[InlineMessageType.ERROR]: 'fieldError',
		[InlineMessageType.SUCCESS]: 'success'
	}

	let { type = InlineMessageType.INFO, message = '', children }: Props = $props()
</script>

<div
	class="inline-message"
	class:info={type === InlineMessageType.INFO}
	class:warning={type === InlineMessageType.WARNING}
	class:error={type === InlineMessageType.ERROR}
	class:success={type === InlineMessageType.SUCCESS}
	role="alert"
>
	<div class="accent-bar"></div>
	<div class="content">
		<div class="icon">
			<Icon name={ICONS[type]} size="md" />
		</div>
		<p class="text">
			{#if children}
				{@render children()}
			{:else}
				{message}
			{/if}
		</p>
	</div>
</div>

<style lang="postcss">
	.inline-message {
		@apply flex w-full overflow-hidden rounded-lg border;
	}

	.inline-message.info {
		@apply bg-[#f4fbff] border-[#1A74A833];
	}
	.inline-message.warning {
		@apply bg-[#fffcf4] border-[#8F6C1A33];
	}
	.inline-message.error {
		@apply bg-[#fdf6f7] border-[#E4485B24];
	}
	.inline-message.success {
		@apply bg-[#F6FCFB] border-[#067A5733];
	}

	.accent-bar {
		@apply w-1 self-stretch shrink-0;
	}

	.inline-message.info .accent-bar {
		@apply bg-[#1A74A8CC];
	}
	.inline-message.warning .accent-bar {
		@apply bg-[#8F6C1ACC];
	}
	.inline-message.error .accent-bar {
		@apply bg-[#E4485BCC];
	}
	.inline-message.success .accent-bar {
		@apply bg-[#067A57CC];
	}

	.content {
		@apply flex items-start gap-3 px-4 py-3;
	}

	.icon {
		@apply shrink-0 w-6 h-6 flex items-center justify-center;
	}

	.inline-message.info .icon :global(svg) {
		@apply text-[#1A74A8];
	}
	.inline-message.warning .icon :global(svg) {
		@apply text-[#8F6C1A];
	}
	.inline-message.error .icon :global(svg) {
		@apply text-[#E4485B];
	}
	.inline-message.success .icon :global(svg) {
		@apply text-[#067A57];
	}

	.text {
		@apply m-0 text-sm text-[#001D33B2] leading-5;
	}
</style>
